// Form builder (R-F1): steps and fields with types, required marks, options and simple «show if» rules; fields the form type needs stay.

import { useEffect, useState } from 'react';
import { FILE_DEFAULTS, uuidv7, type FormDef, type FormField, type FormFieldKind, type FormStep, type FormTypeDef } from '@confdeck/core-shared';
import { useSync } from '../sync/react.tsx';
import { IconButton, Button, Checkbox, Input, Menu, MenuContent, MenuItem, MenuTrigger, TextArea, cx, useToast, Field } from '../ui/primitives.tsx';
import { Icon } from '../ui/icons.tsx';
import s from './forms.module.css';
import b from '../ui/builder.module.css';

export const KIND_LABEL: Record<FormFieldKind, string> = {
  text: 'Строка', textarea: 'Текст', email: 'Email', phone: 'Телефон', number: 'Число', date: 'Дата', url: 'Ссылка',
  select: 'Один из списка', multiselect: 'Несколько из списка', checkbox: 'Галочка', consent: 'Согласие', heading: 'Заголовок',
  record_quantities: 'Количество по вариантам', people: 'Список людей', file: 'Файл',
};
/** Kinds people add by hand; «Количество по вариантам» comes with the form type, it needs a data source. */
const ADDABLE: FormFieldKind[] = ['text', 'textarea', 'email', 'phone', 'number', 'date', 'url', 'select', 'multiselect', 'checkbox', 'consent', 'people', 'file', 'heading'];

const TR: Record<string, string> = { а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'i', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ы: 'y', э: 'e', ю: 'yu', я: 'ya' };
/** A readable machine key from the label: «Размер футболки» → razmer_futbolki. */
export function keyFrom(label: string, taken: Set<string>): string {
  const base = label.toLowerCase().split('').map((c) => TR[c] ?? c).join('').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30) || 'field';
  let k = /^[a-z]/.test(base) ? base : `f_${base}`;
  for (let n = 2; taken.has(k); n++) k = `${base}_${n}`;
  return k;
}

export function FormBuilder({ form, type }: { form: FormDef; type: FormTypeDef | undefined }) {
  const client = useSync();
  const toast = useToast();
  const [steps, setSteps] = useState<FormStep[]>(form.steps);
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => { if (!dirty) setSteps(form.steps); }, [form.steps]);
  const required = new Set(type?.requires ?? []);
  const keys = () => new Set(steps.flatMap((st) => st.fields.map((f) => f.key)));

  const change = (next: FormStep[]) => { setSteps(next); setDirty(true); };
  const patchField = (si: number, fi: number, patch: Partial<FormField>) => change(steps.map((st, i) => (i !== si ? st : { ...st, fields: st.fields.map((f, j) => (j === fi ? { ...f, ...patch } : f)) })));
  const move = (si: number, fi: number, d: -1 | 1) => {
    const list = [...steps[si]!.fields];
    const j = fi + d;
    if (j < 0 || j >= list.length) return;
    [list[fi], list[j]] = [list[j]!, list[fi]!];
    change(steps.map((st, i) => (i === si ? { ...st, fields: list } : st)));
  };
  const add = (si: number, kind: FormFieldKind) => {
    const label = kind === 'heading' ? 'Новый раздел' : kind === 'consent' ? 'Согласен с условиями' : 'Новое поле';
    const f: FormField = { id: uuidv7(), key: keyFrom(label, keys()), label, kind, ...(kind === 'select' || kind === 'multiselect' ? { options: ['Первый вариант', 'Второй вариант'] } : {}), ...(kind === 'people' ? { max: 5 } : {}) };
    change(steps.map((st, i) => (i === si ? { ...st, fields: [...st.fields, f] } : st)));
    setOpen(f.id);
  };
  const remove = (si: number, fi: number) => change(steps.map((st, i) => (i === si ? { ...st, fields: st.fields.filter((_, j) => j !== fi) } : st)));
  const save = async () => {
    const r = await client.run('form.save', { id: form.id, name: form.name, formType: form.formType, scopeId: form.scopeId ?? undefined, steps });
    if (r.ok) { setDirty(false); toast({ kind: 'info', text: 'Форма сохранена' }); } else toast({ kind: 'error', text: r.error.message });
  };

  return (
    <div className={b.builder}>
      {steps.map((st, si) => (
        <section key={st.id} className={b.step}>
          <div className={b.stepHead}>
            <span className={b.stepNo}>Шаг {si + 1}</span>
            <Input bare className={b.stepTitle} value={st.title} aria-label="Название шага" onChange={(e) => change(steps.map((x, i) => (i === si ? { ...x, title: e.target.value } : x)))} />
            {steps.length > 1 && !st.fields.some((f) => f.locked || required.has(f.key)) && (
              <IconButton size="sm" icon="trash" label="Удалить шаг" onClick={() => change(steps.filter((_, i) => i !== si))} />
            )}
          </div>
          {/* R-F3: answers of the «about you» step that the contact has no field for become new fields of the contact. */}
          {type?.process && st.fields.some((f) => f.key === 'email') && <p className="muted">Поля этого шага, которых нет у контакта, при обработке ответа появятся в карточке контакта и заполнятся.</p>}
          <div className={b.fields}>
            {st.fields.map((f, fi) => {
              const fixed = f.locked || required.has(f.key);
              const expanded = open === f.id;
              const earlier = steps.slice(0, si + 1).flatMap((x, i) => (i < si ? x.fields : x.fields.slice(0, fi))).filter((x) => x.kind === 'select' || x.kind === 'checkbox');
              return (
                <div key={f.id} className={cx(b.field, expanded && b.fieldOpen)}>
                  <div className={b.fieldRow}>
                    <span className={b.kind}>{KIND_LABEL[f.kind]}</span>
                    <Input bare className={b.label} value={f.label} aria-label="Подпись поля" onChange={(e) => patchField(si, fi, { label: e.target.value })} />
                    {f.showIf && <span className={s.cond} title="Показывается по условию"><Icon name="filter" size={13} /></span>}
                    {f.kind !== 'heading' && (
                      <Checkbox className={s.req} checked={!!f.required} disabled={required.has(f.key)} onChange={(on) => patchField(si, fi, { required: on })}>Обязательное</Checkbox>
                    )}
                    <IconButton size="sm" icon="chevronUp" label="Вверх" onClick={() => move(si, fi, -1)} />
                    <IconButton size="sm" icon="chevronDown" label="Вниз" onClick={() => move(si, fi, 1)} />
                    <IconButton size="sm" icon="settings" label="Настройки поля" onClick={() => setOpen(expanded ? null : f.id)} />
                    {fixed ? <span className={s.iconGap} aria-hidden /> : <IconButton size="sm" icon="trash" label="Удалить поле" onClick={() => remove(si, fi)} />}
                  </div>
                  {expanded && (
                    <div className={b.fieldMore}>
                      <Field inline label="Подсказка"><Input value={f.help ?? ''} onChange={(e) => patchField(si, fi, { help: e.target.value || undefined })} /></Field>
                      {(f.kind === 'select' || f.kind === 'multiselect') && (
                        <Field inline label="Варианты"><TextArea rows={4} value={(f.options ?? []).join('\n')} onChange={(e) => patchField(si, fi, { options: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })} /></Field>
                      )}
                      {f.kind === 'file' && <Field inline label="Типы файлов"><Input value={(f.accept ?? FILE_DEFAULTS.accept).join(', ')} onChange={(e) => patchField(si, fi, { accept: e.target.value.split(/[\s,;]+/).map((x) => x.replace(/^\./, '').toLowerCase()).filter(Boolean) })} /></Field>}
                      {f.kind === 'file' && <Field inline label="Не больше, МБ"><Input type="number" value={f.maxMb ?? FILE_DEFAULTS.maxMb} onChange={(e) => patchField(si, fi, { maxMb: Math.min(50, Math.max(1, Number(e.target.value) || 1)) })} /></Field>}
                      {f.kind === 'people' && <Field inline label="Не больше"><Input type="number" value={f.max ?? 5} onChange={(e) => patchField(si, fi, { max: Math.max(1, Number(e.target.value) || 1) })} /></Field>}
                      <Field as="div" inline label="Показывать">
                        <Menu>
                          <MenuTrigger asChild><Button size="sm" variant="secondary">{f.showIf ? `если «${earlier.find((x) => x.key === f.showIf!.field)?.label ?? f.showIf.field}» = ${f.showIf.equals === true ? 'да' : Array.isArray(f.showIf.equals) ? f.showIf.equals.join(' или ') : f.showIf.equals}` : 'всегда'}</Button></MenuTrigger>
                          <MenuContent>
                            <MenuItem onSelect={() => patchField(si, fi, { showIf: undefined })}>Всегда</MenuItem>
                            {earlier.flatMap((x) => (x.kind === 'checkbox' ? [{ x, v: true as string | boolean }] : (x.options ?? []).map((o) => ({ x, v: o as string | boolean })))).map(({ x, v }) => (
                              <MenuItem key={`${x.key}:${String(v)}`} onSelect={() => patchField(si, fi, { showIf: { field: x.key, equals: v } })}>Если «{x.label}» = {v === true ? 'да' : String(v)}</MenuItem>
                            ))}
                          </MenuContent>
                        </Menu>
                      </Field>
                      <Field as="div" inline label="Ключ" hint={fixed ? 'поле нужно типу формы, удалить нельзя' : undefined}><code className={b.key}>{f.key}</code></Field>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Menu>
            <MenuTrigger asChild><Button size="sm" variant="ghost" icon="plus" className={b.addField}>Добавить поле</Button></MenuTrigger>
            <MenuContent>{ADDABLE.map((k) => <MenuItem key={k} onSelect={() => add(si, k)}>{KIND_LABEL[k]}</MenuItem>)}</MenuContent>
          </Menu>
        </section>
      ))}
      <div className={b.builderFoot}>
        <Button size="sm" variant="ghost" icon="plus" onClick={() => change([...steps, { id: uuidv7(), title: `Шаг ${steps.length + 1}`, fields: [] }])}>Добавить шаг</Button>
        <span className={b.spacer} />
        {dirty && <Button size="sm" variant="ghost" onClick={() => { setSteps(form.steps); setDirty(false); }}>Отменить</Button>}
        <Button size="sm" variant="primary" disabled={!dirty} onClick={() => void save()}>Сохранить поля</Button>
      </div>
    </div>
  );
}
